import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { actions, selectors } from 'darwin_core';
import { MODES } from 'darwin_core/utils/consts.js';

import BackLink from '../navigation/BackLink.jsx';
import { zoneActionPath, zonePath } from '../navigation/paths.js';
import { useZoneRouteContext } from '../navigation/ZoneRoute.jsx';
import { useWebSocket } from '../transport/WebSocketProvider.jsx';
import { SpinnerIcon } from '../components/icons.jsx';
import ModalShell from '../components/ModalShell.jsx';
import useScreen from '../hooks/useScreen.js';
import ZoneSchedule from '../dashboard/ZoneSchedule.jsx';
import { describeEventTarget } from '../dashboard/formatSchedule.js';
import EventEditorModal from '../schedule/EventEditorModal.jsx';
import PairedEventEditorModal from '../schedule/PairedEventEditorModal.jsx';
import CopyDayModal from '../schedule/CopyDayModal.jsx';
import CopyWeekModal from '../schedule/CopyWeekModal.jsx';
import { ZoneHolidayBanner } from '../components/HolidayBanner.jsx';
import { OfflineHelp, useZoneOfflineHelp } from '../components/OfflineHelp.jsx';
import {
  WEEK_DAYS,
  flattenReported,
  formatTime,
  canAddToDay,
  dayHasEvents,
  copyDayToDays,
  getScheduleKind,
  getKindAdapter,
} from '../schedule/scheduleEditModel.js';

const COPY_WEEK_OPTS = { excludeThisDevice: true, ownerRoleOnly: false };

// Content equality for the live copy-week target list (getOtherDevicesSameType
// rebuilds every object per read): same zones, names and lock state.
function sameCopyTargets(a, b) {
  if (a === b) {
    return true;
  }
  if (!a || !b || a.length !== b.length) {
    return false;
  }
  return a.every((target, i) => {
    const other = b[i];
    return (
      target.homeId === other.homeId &&
      target.zoneId?.join('_') === other.zoneId?.join('_') &&
      target.friendlyName === other.friendlyName &&
      target.settingsLocked === other.settingsLocked
    );
  });
}

// The zone's weekly schedule editor. Edits accumulate in a LOCAL DRAFT
// (setDraftSchedule) and nothing reaches the device until "Save schedule changes"
// commits the draft over the WebSocket (setSchedule); leaving with unsaved edits
// prompts to discard. All four editing models (SI heating/hot-water, HPI paired
// heating/hot-water) are handled through a per-kind adapter (getKindAdapter) so
// this screen never re-branches on kind. A zone with no schedule yet falls back to
// the read-only weekly view.
//
// While this zone's thermostat is in holiday the editor is LOCKED: nothing here may
// reach the device (darwin_core's setSchedule has no holiday guard of its own, and
// the app makes the screen unreachable by blocking the zone tile). The week stays
// visible read-only with the Holiday notice explaining why; only Discard stays
// live, so a draft begun before the holiday started is not stranded. The same
// lock applies outside Auto, where the app does not offer the schedule at all.
export default function ScheduleScreen() {
  const headingRef = useScreen('Schedule');
  const { homeId, zoneId } = useZoneRouteContext();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ws = useWebSocket();
  const sendWsMessage = ws?.sendWsMessage ?? (() => {});

  const name = useSelector((s) =>
    selectors.getPrtFriendlyName(s, homeId, zoneId),
  );
  const isDhw = useSelector((s) => selectors.isDhw(s, homeId, zoneId));
  const isHpi = useSelector((s) => selectors.isHpi(s, homeId, zoneId));
  const mode = useSelector((s) => selectors.getMode(s, homeId, zoneId));
  const minSetpoint = useSelector((s) =>
    selectors.getMinSetpoint(s, homeId, zoneId),
  );
  const maxSetpoint = useSelector((s) =>
    selectors.getMaxSetpoint(s, homeId, zoneId),
  );
  const is24Hour = useSelector((s) =>
    selectors.getTimeFormat(s, homeId, zoneId),
  );
  const saving = useSelector((s) =>
    selectors.isScheduleSaveInProgress(s, homeId, zoneId),
  );
  // This thermostat's own holiday state (per zone — see holidayStatus.js), coerced
  // as useZoneControls does.
  const inHoliday = useSelector((s) =>
    Boolean(selectors.isInHolidayMode(s, homeId, zoneId)),
  );
  const offlineHelp = useZoneOfflineHelp(homeId, zoneId);
  const comfortSetpoint = useSelector(
    (s) => selectors.getHpSetpoint(s, homeId, zoneId).comfortSetpoint,
  );
  const reducedSetpoint = useSelector(
    (s) => selectors.getHpSetpoint(s, homeId, zoneId).reducedSetpoint,
  );

  const kind = getScheduleKind(isDhw, isHpi);
  const adapter = useMemo(() => getKindAdapter(kind), [kind]);
  const hpSetpoints = useMemo(
    () => ({ comfortSetpoint, reducedSetpoint }),
    [comfortSetpoint, reducedSetpoint],
  );

  // Raw reported schedule read as a stable store reference (getZone returns the
  // stored zone object; parseZone normalises both `schedule` and DHW's
  // `dhwSchedule` onto `.schedule`). The draft is a stable reference too.
  const rawSchedule = useSelector(
    (s) => selectors.getZone(s, homeId, zoneId)?.schedule,
  );
  const draft = useSelector((s) =>
    selectors.getDraftSchedule(s, homeId, zoneId),
  );

  const actualSchedule = useMemo(
    () => flattenReported(rawSchedule),
    [rawSchedule],
  );
  const working = draft !== undefined ? draft : actualSchedule;
  const hasDraft = draft !== undefined;

  const [editing, setEditing] = useState(null);
  const [confirmingExit, setConfirmingExit] = useState(false);
  const [copyDay, setCopyDay] = useState(null);
  const [copyWeekOpen, setCopyWeekOpen] = useState(false);
  const [copyRequestId, setCopyRequestId] = useState('none');
  const [saveRequestId, setSaveRequestId] = useState('none');

  const hasCopyTargets = useSelector(
    (s) =>
      selectors.getOtherDevicesSameType(s, homeId, zoneId, COPY_WEEK_OPTS)
        .length > 0,
  );
  // The target list is read LIVE while the modal is open, not snapshotted when it
  // opens: CopyWeekModal disables a target whose settingsLocked flips (it entered
  // Holiday / Override / went offline) and that only works if the list it re-reads
  // actually updates. null while closed.
  const copyWeekTargets = useSelector(
    (s) =>
      copyWeekOpen
        ? selectors.getOtherDevicesSameType(s, homeId, zoneId, COPY_WEEK_OPTS)
        : null,
    sameCopyTargets,
  );
  const copyStatus = useSelector((s) =>
    selectors.getScheduleUpdateState(s, copyRequestId),
  );
  const copyError = useSelector((s) =>
    selectors.getScheduleUpdateError(s, copyRequestId),
  );
  const copyLoading =
    useSelector((s) =>
      selectors.getScheduleUpdateInProgress(s, copyRequestId),
    ) && !copyError;
  // The footer save shares the same global request slot as copy-week; the two are
  // gated against each other (below) so at most one is ever active, which keeps
  // this error read unambiguous.
  const saveError = useSelector((s) =>
    selectors.getScheduleUpdateError(s, saveRequestId),
  );

  // Navigate back to the zone once a save completes (was saving → no longer
  // saving, draft cleared by the confirming frame). On a FAILED save the draft is
  // kept, so hasDraft stays true and we correctly do NOT navigate away.
  const wasSaving = useRef(false);
  useEffect(() => {
    if (saving) {
      wasSaving.current = true;
    } else if (wasSaving.current) {
      wasSaving.current = false;
      if (!hasDraft) {
        navigate(zonePath(homeId, zoneId));
      }
    }
  }, [saving, hasDraft, navigate, homeId, zoneId]);

  const editable = working.length > 0;
  // Locked while away, outside Auto, and while the zone cannot be reached. The
  // app only opens the schedule from Auto — its Schedule panel is disabled in
  // every other mode (an override, Manual, Off, or a cylinder that is On/Off),
  // because the schedule is not what is running the zone then. A browser user can
  // still arrive here by URL, so the week stays visible read-only and the notice
  // below says why. Every write control disables on `locked`, so an unreachable
  // zone cannot be saved to (the frame could not get there) while Discard stays
  // live and a draft begun before the outage is not stranded.
  const notInAuto = mode !== MODES.Auto;
  const locked = inHoliday || notInAuto || offlineHelp.show;
  const canEdit = editable && !locked;
  const noun = isDhw ? 'hot water' : 'heating';

  const applyDraft = (newSchedule) => {
    dispatch(
      actions.setDraftSchedule(homeId, zoneId, newSchedule, actualSchedule),
    );
    // A fresh edit clears any stale "save failed" message.
    setSaveRequestId('none');
    setEditing(null);
  };

  const handleSave = (payload) => {
    try {
      const next =
        editing.mode === 'add'
          ? adapter.applyAdd(working, editing.day, payload)
          : adapter.applyEdit(working, editing.day, editing.existing, payload);
      applyDraft(next);
    } catch {
      // Defensive: a malformed/partial schedule can make a darwin_core helper
      // throw (e.g. adding a last-of-day event when the next day is empty — never
      // true of real device data). Close without corrupting state.
      setEditing(null);
    }
  };

  const handleDelete = () => {
    applyDraft(adapter.applyDelete(working, editing.day, editing.existing));
  };

  const commit = () => {
    if (!hasDraft || saving || copyLoading || locked) {
      return;
    }
    const rid = uuidv4();
    setSaveRequestId(rid);
    dispatch(
      actions.setSchedule(sendWsMessage, homeId, zoneId, working, isHpi, rid),
    );
  };

  const discard = () => {
    dispatch(actions.clearDraftSchedule(homeId, zoneId));
    setConfirmingExit(false);
    navigate(zonePath(homeId, zoneId));
  };

  const handleBack = () => {
    if (hasDraft) {
      setConfirmingExit(true);
    } else {
      navigate(zonePath(homeId, zoneId));
    }
  };

  const applyCopyDay = (toDays) => {
    applyDraft(copyDayToDays(working, copyDay, toDays));
    setCopyDay(null);
  };

  const openCopyWeek = () => setCopyWeekOpen(true);

  const closeCopyWeek = () => {
    setCopyWeekOpen(false);
    setCopyRequestId('none');
    dispatch(actions.clearScheduleUpdateRequest());
  };

  // Holiday can start from another client (or the thermostat itself) while the
  // modal is open. The screen locks underneath it, so the modal closes rather than
  // offering a copy the lock forbids.
  useEffect(() => {
    if (locked && copyWeekOpen) {
      setCopyWeekOpen(false);
    }
  }, [locked, copyWeekOpen]);

  const doCopyWeek = (target) => {
    // Re-checked at dispatch: this is the last point before a schedule leaves for
    // the target, and setSchedule itself has no holiday guard.
    if (locked || !target || target.settingsLocked) {
      return;
    }
    const rid = uuidv4();
    setCopyRequestId(rid);
    dispatch(
      actions.setSchedule(
        sendWsMessage,
        target.homeId,
        target.zoneId,
        working,
        isHpi,
        rid,
      ),
    );
  };

  useEffect(() => {
    if (copyRequestId !== 'none' && copyStatus === 'Confirmed') {
      dispatch(actions.clearScheduleUpdateRequest());
      setCopyRequestId('none');
      setCopyWeekOpen(false);
    }
  }, [copyRequestId, copyStatus, dispatch]);

  const openEdit = (day, row) =>
    setEditing({ mode: 'edit', day, existing: adapter.rowToExisting(row) });

  return (
    <main className="app-shell screen schedule-screen">
      <header className="screen__header">
        {hasDraft ? (
          <BackLink onClick={handleBack} label={name || 'Zone'} />
        ) : (
          <BackLink to={zonePath(homeId, zoneId)} label={name || 'Zone'} />
        )}
        <h1 id="main-content" tabIndex={-1} ref={headingRef}>
          Schedule
        </h1>
        <p className="screen__sub">
          {canEdit
            ? `Add, edit and remove your ${noun} times.`
            : `Your weekly ${noun} times.`}
        </p>
        {editable && hasCopyTargets && (
          <button
            type="button"
            className="schedule-copy-week"
            disabled={saving || locked}
            onClick={openCopyWeek}
          >
            Copy to another zone
          </button>
        )}
      </header>

      <ZoneHolidayBanner
        homeId={homeId}
        zoneId={zoneId}
        detail="Cancel Holiday to change the schedule."
      />

      {/* One reason for the lock at a time. An unreachable zone comes first — it
          is the one the user must act on, and the mode behind the Auto notice is
          only the last one the zone got to report. Holiday comes next: while away
          the Holiday notice already explains the lock, so the Auto notice is only
          shown otherwise. */}
      {offlineHelp.show ? (
        <OfflineHelp {...offlineHelp} />
      ) : (
        notInAuto &&
        !inHoliday && (
          <section className="schedule-mode-lock" aria-label="Schedule locked">
            <p>
              Your {noun} is not in Auto, so this schedule is not running.
              Switch to Auto to change it.{' '}
              <Link to={zoneActionPath(homeId, zoneId, 'status')}>
                Change {noun} status
              </Link>
            </p>
          </section>
        )
      )}

      {!editable ? (
        <>
          <ZoneSchedule homeId={homeId} zoneId={zoneId} />
          <p className="schedule-screen__soon">
            No schedule has been reported for this zone yet.
          </p>
        </>
      ) : (
        <>
          <ol className="schedule-days">
            {WEEK_DAYS.map((day) => {
              const events = adapter.rowsForDay(working, day);
              return (
                <li key={day} className="schedule-day">
                  <div className="schedule-day__head">
                    <h2 className="schedule-day__name">{day}</h2>
                    <div className="schedule-day__actions">
                      <button
                        type="button"
                        className="schedule-day__add"
                        disabled={locked || !canAddToDay(working, day)}
                        onClick={() => setEditing({ mode: 'add', day })}
                      >
                        Add event
                      </button>
                      <button
                        type="button"
                        className="schedule-day__add"
                        disabled={locked || !dayHasEvents(working, day)}
                        onClick={() => setCopyDay(day)}
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                  {events.length === 0 ? (
                    <p className="schedule-day__none">No events</p>
                  ) : (
                    <ul className="schedule-day__events">
                      {events.map((event) => {
                        const { label } = describeEventTarget(
                          event,
                          hpSetpoints,
                        );
                        return (
                          <li key={event.startTime}>
                            <button
                              type="button"
                              className="schedule-event"
                              disabled={locked}
                              onClick={() => openEdit(day, event)}
                            >
                              <span className="schedule-event__time">
                                {formatTime(event.startTime, is24Hour)}
                                {adapter.paired &&
                                  `–${formatTime(event.endTime, is24Hour)}`}
                              </span>
                              <span className="schedule-event__target">
                                {label}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>

          <div className="schedule-footer">
            {saveError && !saving && (
              <p className="schedule-footer__error" role="alert">
                {saveError}
              </p>
            )}
            <button
              type="button"
              className="schedule-footer__save"
              disabled={!hasDraft || saving || copyLoading || locked}
              onClick={commit}
            >
              {saving ? (
                <>
                  <SpinnerIcon />
                  Saving…
                </>
              ) : (
                'Save schedule changes'
              )}
            </button>
            {hasDraft && !saving && (
              <button
                type="button"
                className="schedule-footer__discard"
                onClick={() => setConfirmingExit(true)}
              >
                Discard changes
              </button>
            )}
          </div>
        </>
      )}

      {editing &&
        (adapter.paired ? (
          <PairedEventEditorModal
            mode={editing.mode}
            day={editing.day}
            kind={kind}
            working={working}
            is24Hour={is24Hour}
            hpTemps={hpSetpoints}
            existing={editing.existing}
            canDelete={
              editing.mode === 'edit' && adapter.canDelete(working, editing.day)
            }
            onSave={handleSave}
            onDelete={handleDelete}
            onClose={() => setEditing(null)}
          />
        ) : (
          <EventEditorModal
            mode={editing.mode}
            day={editing.day}
            isDhw={adapter.isDhw}
            working={working}
            is24Hour={is24Hour}
            minSetpoint={minSetpoint}
            maxSetpoint={maxSetpoint}
            existing={editing.existing}
            canDelete={
              editing.mode === 'edit' && adapter.canDelete(working, editing.day)
            }
            onSave={handleSave}
            onDelete={handleDelete}
            onClose={() => setEditing(null)}
          />
        ))}

      {copyDay && (
        <CopyDayModal
          fromDay={copyDay}
          onCopy={applyCopyDay}
          onClose={() => setCopyDay(null)}
        />
      )}

      {copyWeekTargets && (
        <CopyWeekModal
          targets={copyWeekTargets}
          loading={copyLoading}
          error={copyError}
          onCopy={doCopyWeek}
          onClose={closeCopyWeek}
        />
      )}

      {confirmingExit && (
        <ModalShell
          labelledBy="discard-title"
          className="schedule-modal--confirm"
          onClose={() => setConfirmingExit(false)}
        >
          <h2 id="discard-title" className="schedule-modal__title">
            Discard schedule changes?
          </h2>
          <p className="schedule-modal__desc">
            You have unsaved changes to this schedule.
          </p>
          <div className="schedule-modal__actions">
            <button
              type="button"
              className="schedule-modal__cancel"
              onClick={() => setConfirmingExit(false)}
            >
              Keep editing
            </button>
            <button
              type="button"
              className="schedule-modal__delete-btn"
              onClick={discard}
            >
              Discard
            </button>
          </div>
        </ModalShell>
      )}
    </main>
  );
}
